import React, { createContext, useContext, ReactNode, useState, useEffect, useRef } from "react";
import { Config } from "../types";
import createMyTheme from "../themes/theme";
import { Theme } from "@mui/material/styles";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { CircularProgress, Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { enqueueUniqueSnackbar } from "../utilities/snackbarUtils";
import ExpandableErrorSnackbar from "../components/common/ExpandableErrorSnackbar";
import { useAuthSelector } from "../store/auth";
import { apiFetch, REACT_APP_API_ROOT } from "../utilities/api";
import { adaptRemoteConfig, isRemoteConfig } from "../utilities/configAdapter";

/** Verbose config diagnostics are dev-only; production stays quiet and secret-free. */
const IS_DEV = process.env.NODE_ENV !== "production";

/** Dev-only logger for config loading diagnostics (no-op in production builds). */
const debugConfig = (...args: unknown[]) => {
  if (IS_DEV) console.log("[ConfigContext]", ...args);
};

/** Strips secrets (auth credentials) from a config object before it is logged. */
const redactConfigForLog = (cfg: unknown) => {
  if (!cfg || typeof cfg !== "object") return cfg;
  const { auth, authConfig, ...safe } = cfg as Record<string, unknown>;
  return safe;
};

// Create a type that combines Config with the update function
type ConfigContextType = Config & {
  updateConfig: (updates: Partial<Config>) => void;
  /**
   * Re-fetch the database-driven config from the API and apply it, without
   * gating the whole app behind the initial-load spinner. Used after a form is
   * created/published so the new CmTable's map layer + endpoint appear without
   * a manual page reload. No-op-ish for local/default config (there's no new
   * remote table to pick up).
   */
  refetchConfig: () => Promise<void>;
};

const ConfigContext = createContext<ConfigContextType | null>(null);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
  configUrlParam?: string;
}

// Keys for session storage
const ADMIN_SETTINGS_KEY = "culturemap-admin-settings";

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children, configUrlParam = "config" }) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [theme, setTheme] = useState<Theme | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Gates rendering while a config load is in flight so the app never mounts with a
  // stale/default config that is later swapped for the API config (avoids UI glitches).
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user: profile, isInitialized } = useAuthSelector();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Prevents redundant reloads when only unrelated deps (e.g. snackbar identities) change.
  const loadKeyRef = useRef<string | null>(null);
  // Monotonic id for each triggered load. A load only applies its result if its id is
  // still the latest — this discards superseded in-flight loads (e.g. an older org's
  // config resolving after the user has switched orgs) so we never apply the wrong config.
  const loadRunIdRef = useRef(0);

  /** True when the load identified by `runId` has been superseded by a newer load. */
  const isStaleLoad = (runId: number) => loadRunIdRef.current !== runId;

  // Helper function to show expandable error snackbars
  const showConfigError = (simplifiedMessage: string, fullErrorDetails: string) => {
    const snackbarKey = enqueueSnackbar("", {
      persist: false,
      autoHideDuration: 10000,
      content: (key) => (
        <ExpandableErrorSnackbar
          id={key}
          message={simplifiedMessage}
          fullErrorDetails={fullErrorDetails}
          dismissSnackbar={() => closeSnackbar(key)}
        />
      )
    });
    return snackbarKey;
  };

  const validateConfig = (config: any): config is Config => {
    if (!config || typeof config !== "object") {
      throw new Error("Invalid config format: Not an object");
    }

    // Basic validation for essential properties
    if (!config.appTitle) {
      throw new Error("Invalid config format: Missing appTitle");
    }

    // Validate map layers if they exist
    if (config.mapLayers && Array.isArray(config.mapLayers)) {
      for (let i = 0; i < config.mapLayers.length; i++) {
        const layer = config.mapLayers[i];

        // Validate layer ID
        if (!layer.id) {
          throw new Error(`Invalid map layer at index ${i}: Missing id`);
        }

        // Validate layer style expressions
        if (layer.style) {
          // Check for zoom-dependent interpolate expressions
          Object.entries(layer.style).forEach(([key, value]) => {
            if (Array.isArray(value) && value[0] === "interpolate" && Array.isArray(value[2]) && value[2][0] === "zoom") {
              // For interpolate expressions, check that zoom levels are in ascending order
              const zoomLevels = [];
              for (let j = 3; j < value.length; j += 2) {
                zoomLevels.push(value[j]);
              }

              // Check if zoom levels are in ascending order
              for (let j = 1; j < zoomLevels.length; j++) {
                if (zoomLevels[j] <= zoomLevels[j - 1]) {
                  throw new Error(
                    `Invalid config in layer "${layer.id}", style property "${key}": ` +
                      `Zoom levels in interpolate expression must be in ascending order. ` +
                      `Found ${zoomLevels[j - 1]} followed by ${zoomLevels[j]}.`
                  );
                }
              }
            }
          });
        }
      }
    }

    return true;
  };

  // Load admin settings from session storage
  const loadAdminSettings = () => {
    try {
      const storedSettings = localStorage.getItem(ADMIN_SETTINGS_KEY);
      if (storedSettings) {
        return JSON.parse(storedSettings);
      }
    } catch (error) {
      console.warn("Failed to load admin settings from session storage:", error);
    }
    return {};
  };

  // Save admin settings to session storage
  const saveAdminSettings = (newSettings: Partial<Config>) => {
    try {
      // Only save specific admin settings, not the entire config
      const adminSettings = {
        showMediaInRelatedItemsCloud: newSettings.showMediaInRelatedItemsCloud,
        showGeoInRelatedItemsCloud: newSettings.showGeoInRelatedItemsCloud,
        showIndirectRelationshipsInRing: newSettings.showIndirectRelationshipsInRing,
        terrainExaggeration: newSettings.terrainExaggeration
      };

      // Filter out undefined values
      const filteredSettings = Object.fromEntries(Object.entries(adminSettings).filter(([_, value]) => value !== undefined));

      const storedSettings = loadAdminSettings();
      const mergedSettings = { ...storedSettings, ...filteredSettings };

      localStorage.setItem(ADMIN_SETTINGS_KEY, JSON.stringify(mergedSettings));
    } catch (error) {
      console.warn("Failed to save admin settings to session storage:", error);
    }
  };

  const loadDefaultConfig = async (isOrgFallback: boolean = false, runId?: number) => {
    try {
      const defaultResponse = await fetch("/configs/defaultConfig.json");
      if (!defaultResponse.ok) {
        enqueueUniqueSnackbar(enqueueSnackbar, `Failed to load default config: ${defaultResponse.status}`, {
          variant: "error",
          autoHideDuration: 6000
        });
        throw new Error(`Failed to load default config: ${defaultResponse.status}`);
      }

      const defaultConfig = await defaultResponse.json();
      try {
        validateConfig(defaultConfig);

        defaultConfig.initialMapView = defaultConfig.initialMapView || {
          longitude: 133.7751,
          latitude: -25.2744,
          zoom: 3.5,
          pitch: 0
        };

        // If this is a fallback for an organization without a config,
        // clear potentially sensitive API endpoints to prevent cross-organization data access
        if (isOrgFallback && profile?.organisation?.id) {
          console.warn(
            `[ConfigContext] Loading safe fallback config for org ${profile.organisation.id} - clearing ${defaultConfig.apiEndpoints?.length || 0} API endpoints to prevent cross-organization data access`
          );
          defaultConfig.apiEndpoints = [];
          defaultConfig.appTitle = `${defaultConfig.appTitle} - Organization ${profile.organisation.id}`;

          // Add a notice about missing configuration
          enqueueUniqueSnackbar(
            enqueueSnackbar,
            `Configuration missing for your organisation. Please contact support to set up your data sources.`,
            { variant: "info", autoHideDuration: 10000 }
          );
        }

        // Apply stored admin settings if available
        const adminSettings = loadAdminSettings();
        const mergedConfig = { ...defaultConfig, ...adminSettings };

        // Drop the result if a newer load has superseded this one.
        if (runId !== undefined && isStaleLoad(runId)) {
          debugConfig(`Discarding superseded default-config load (run ${runId}, current ${loadRunIdRef.current}).`);
          return;
        }

        setConfig(mergedConfig);
        setTheme(createMyTheme(mergedConfig));
        setError(null);
      } catch (validationError) {
        const message = validationError instanceof Error ? validationError.message : "Invalid default config format";
        console.error("Default config validation error:", validationError);
        setError(message);
        showConfigError("Default config error, using backup configuration", `Default config validation error: ${message}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load default config";
      console.error("Failed to load default config:", error);
      setError(message);
      showConfigError("Fatal error loading configuration", `Failed to load any configuration: ${message}`);
    }
  };

  // Primary source: database-driven config from the backend (after login).
  // Returns true when config was loaded & applied; false to signal the caller to fall back.
  const loadRemoteConfig = async (runId?: number): Promise<boolean> => {
    const root = REACT_APP_API_ROOT.replace(/\/+$/, "");
    const configUrl = `${root}/api/v2/config/`;
    try {
      debugConfig(`Fetching config from API: ${configUrl}`);
      const raw = await apiFetch(configUrl);

      if (!isRemoteConfig(raw)) {
        console.warn("[ConfigContext] /api/v2/config/ response missing/invalid schema_version — falling back to default config.");
        return false;
      }

      const adapted = adaptRemoteConfig(raw);
      // Log only redacted, non-secret diagnostics, and only in development.
      debugConfig("Adapted config (legacy shape):", redactConfigForLog(adapted));
      debugConfig(`Adapted apiEndpoints: ${adapted.apiEndpoints?.length ?? 0}, mapLayers: ${adapted.mapLayers?.length ?? 0}`);

      validateConfig(adapted);

      // Apply stored admin settings on top of the API config.
      const adminSettings = loadAdminSettings();
      const mergedConfig = { ...adapted, ...adminSettings };

      // Drop the result if a newer load has superseded this one.
      if (runId !== undefined && isStaleLoad(runId)) {
        debugConfig(`Discarding superseded API-config load (run ${runId}, current ${loadRunIdRef.current}).`);
        return true;
      }

      setConfig(mergedConfig);
      setTheme(createMyTheme(mergedConfig));
      setError(null);
      debugConfig("Config from API applied successfully.");
      return true;
    } catch (fetchError) {
      console.error(`[ConfigContext] Error loading config from API (${configUrl}):`, fetchError);
      return false;
    }
  };

  useEffect(() => {
    const loadConfig = async (runId: number) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams(window.location.search);
        const configParamFromUrl = params.get(configUrlParam);

        // Check env var for local config (for local dev without needing ?config=local in URL)
        const useLocalConfigEnv = process.env.REACT_APP_USE_LOCAL_CONFIG === "true";

        // URL param takes precedence, then env var
        const configParam = configParamFromUrl || (useLocalConfigEnv ? "local" : null);

        if (configParam) {
          try {
            // Handle local config case
            if (configParam === "local" && profile?.organisation?.id) {
              const localConfigUrl = `/customers/${profile.organisation.id}/config.json`;
              try {
                const response = await fetch(localConfigUrl);

                if (!response.ok) {
                  console.warn(`Organisation config not found for org ${profile.organisation.id}, falling back to default`);
                  enqueueUniqueSnackbar(
                    enqueueSnackbar,
                    `No configuration found for your organisation (ID: ${profile.organisation.id}). Using default settings.`,
                    { variant: "warning", autoHideDuration: 8000 }
                  );
                  await loadDefaultConfig(true, runId);
                  return;
                }

                const localConfig = await response.json();
                try {
                  validateConfig(localConfig);
                  if (isStaleLoad(runId)) {
                    debugConfig(`Discarding superseded local-config load (run ${runId}, current ${loadRunIdRef.current}).`);
                    return;
                  }
                  setConfig(localConfig);
                  setTheme(createMyTheme(localConfig));
                  setError(null);
                } catch (validationError) {
                  const message = validationError instanceof Error ? validationError.message : "Unknown validation error";
                  console.error("Organisation config validation error:", validationError);
                  showConfigError("Organisation config error, using default config", `Configuration validation failed: ${message}`);
                  await loadDefaultConfig(false, runId);
                }
                return;
              } catch (fetchError) {
                console.error("Error fetching local config:", fetchError);
                enqueueUniqueSnackbar(enqueueSnackbar, `Error loading organisation config`, { variant: "error", autoHideDuration: 5000 });
                await loadDefaultConfig(false, runId);
                return;
              }
            }

            // "local" requested but no authenticated org yet (e.g. login screen).
            // Do NOT fall through to the generic branch below (which would try to
            // fetch "/local" and fail); use the default config until the user logs in.
            if (configParam === "local") {
              debugConfig("'local' config requested but no authenticated org yet — using default config (pre-login).");
              await loadDefaultConfig(false, runId);
              return;
            }

            // Handle other config URLs
            try {
              const configUrl = configParam.startsWith("http")
                ? configParam
                : `/${configParam.startsWith("/") ? configParam.slice(1) : configParam}`;

              try {
                const response = await fetch(configUrl);

                if (!response.ok) {
                  enqueueUniqueSnackbar(enqueueSnackbar, `Failed to load config, falling back to default`, {
                    variant: "warning",
                    autoHideDuration: 5000
                  });
                  await loadDefaultConfig(false, runId);
                  return;
                }

                const loadedConfig = await response.json();
                try {
                  validateConfig(loadedConfig);
                  if (isStaleLoad(runId)) {
                    debugConfig(`Discarding superseded custom-config load (run ${runId}, current ${loadRunIdRef.current}).`);
                    return;
                  }
                  setConfig(loadedConfig);
                  setTheme(createMyTheme(loadedConfig));
                  setError(null);
                } catch (validationError) {
                  const message = validationError instanceof Error ? validationError.message : "Unknown validation error";
                  console.error("Config validation error:", validationError);
                  showConfigError("Config error, using default config", `Configuration validation failed: ${message}`);
                  await loadDefaultConfig(false, runId);
                }
                return;
              } catch (fetchError) {
                console.error("Error fetching config:", fetchError);
                enqueueUniqueSnackbar(enqueueSnackbar, `Error loading config`, { variant: "error", autoHideDuration: 5000 });
                await loadDefaultConfig(false, runId);
                return;
              }
            } catch (error) {
              console.error("Error processing custom config URL:", error);
              enqueueUniqueSnackbar(enqueueSnackbar, `Error processing config URL`, { variant: "error", autoHideDuration: 5000 });
              await loadDefaultConfig(false, runId);
              return;
            }
          } catch (error) {
            console.error("Top-level error handling config param:", error);
            enqueueUniqueSnackbar(enqueueSnackbar, `Config loading error`, { variant: "error", autoHideDuration: 5000 });
            await loadDefaultConfig(false, runId);
            return;
          }
        }

        // Primary source: database-driven config from the backend API (post-login).
        if (profile?.organisation?.id) {
          debugConfig(`Authenticated (org ${profile.organisation.id}) — attempting API config.`);
          const loadedFromApi = await loadRemoteConfig(runId);
          if (loadedFromApi) {
            return;
          }

          // No config for this org, or the API call failed → safe default fallback.
          console.warn(`[ConfigContext] API config unavailable for org ${profile.organisation.id} — loading default config fallback.`);
          enqueueUniqueSnackbar(
            enqueueSnackbar,
            `No configuration found for your organisation (ID: ${profile.organisation.id}). Using default settings.`,
            { variant: "warning", autoHideDuration: 8000 }
          );
          await loadDefaultConfig(true, runId);
          return;
        }

        debugConfig("No authenticated org — loading default config (pre-login/anon).");
        await loadDefaultConfig(false, runId);
      } catch (error) {
        console.error("Fatal error in config loading:", error);
        showConfigError(
          "Fatal error in configuration loading",
          `A fatal error occurred when loading configuration: ${error instanceof Error ? error.message : "Unknown error"}`
        );
        await loadDefaultConfig(false, runId);
      } finally {
        // Only the latest load owns the loading gate; a superseded load must not flip it.
        if (!isStaleLoad(runId)) {
          setIsLoading(false);
        }
      }
    };

    if (isInitialized) {
      const params = new URLSearchParams(window.location.search);
      const configParam = params.get(configUrlParam) || (process.env.REACT_APP_USE_LOCAL_CONFIG === "true" ? "local" : "");
      const orgId = profile?.organisation?.id ?? "anon";
      const loadKey = `${configParam}|${orgId}`;

      // Skip redundant reloads triggered by unrelated dependency identity changes.
      if (loadKeyRef.current !== loadKey) {
        debugConfig(`Load triggered (key: "${loadKey}", previous: "${loadKeyRef.current ?? "none"}").`);
        loadKeyRef.current = loadKey;
        const runId = ++loadRunIdRef.current;
        loadConfig(runId);
      } else {
        debugConfig(`Skipping reload — load key unchanged ("${loadKey}").`);
      }
    }
  }, [configUrlParam, profile, isInitialized, enqueueSnackbar, closeSnackbar]);

  const updateConfig = (updates: Partial<Config>) => {
    if (!config) return;

    try {
      // Create a new config object with the updates
      const newConfig = { ...config, ...updates };

      // Validate the new config
      validateConfig(newConfig);

      // Apply the validated updates
      setConfig(newConfig);
      setTheme(createMyTheme(newConfig));

      // Save admin settings to session storage
      saveAdminSettings(updates);
    } catch (error) {
      console.error("Config update validation error:", error);
      showConfigError(
        "Failed to update configuration",
        `Configuration update validation failed: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  // Silent re-fetch of the API config (no full-screen spinner). Bumps the run id
  // so it counts as the latest load and isn't discarded as stale.
  const refetchConfig = async () => {
    const runId = ++loadRunIdRef.current;
    await loadRemoteConfig(runId);
  };

  if (isLoading || !config || !theme) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
        {error && (
          <Typography color='error' sx={{ ml: 2 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Create a context value that combines the config properties with the update function
  const contextValue: ConfigContextType = {
    ...config,
    updateConfig,
    refetchConfig
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ConfigContext.Provider>
  );
};
